<template>
  <div ref="container" class="audio-recorder">
    <inline-loader v-if="isLoading" />
    <template v-else>
      <cv-inline-notification
        v-if="hasError"
        kind="error"
        :title="error.message"
        :action-label="errorActionLabel"
        low-contrast
        @action="handleOpenHelp"
        @close="handleCloseError"
      >
        <template #subtitle>
          <div v-safe-html="error.subMessage" />
        </template>
      </cv-inline-notification>

      <div v-show="!realError" class="duration">
        {{ recordingDuration }}
      </div>

      <div class="tools">
        <button v-show="!isRecording" class="btn-record" :disabled="realError">
          <MicrophoneFilled32 class="sls-icon" />
        </button>
        <fetch-data-button
          :class="{
            'btn-upload': pendingUpload,
            'btn-stop': !pendingUpload,
          }"
          :kind="dataSaveButton.kind"
          :loading="isProcessing"
          :completed="dataSaveButton.completed"
          :error="dataSaveButton.error"
          :error-message="uploadErrorMessage"
          :disabled="realError || isProcessing"
          :icon-only="dataSaveButton.iconOnly"
          :tip-alignment="dataSaveButton.tipAlignment"
          :tip-position="dataSaveButton.tipPosition"
          @click="handleStop"
          @reset-error-state="handleSaveButtonResetError"
        >
          <component :is="iconName" class="sls-icon" />
        </fetch-data-button>
        <button v-if="pendingUpload || hasError" class="btn-close" @click="handleClose">
          <span class="icon">
            <Close24 class="sls-icon" />
          </span>
        </button>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import AudioRecorder from "../audio-recorder";
import Close24 from "@images/icons/action/24px/Close24.svg?component";
import MicrophoneFilled32 from "@images/icons/indicative/32px/MicrophoneFilled32.svg?component";
import Stop24 from "@images/icons/action/24px/Stop24.svg?component";
import Upload24 from "@images/icons/action/24px/Upload24.svg?component";
import { isEmpty } from "@utils/var.check";
import { MEDIA_TYPE } from "@modules/editor/service/editor.constant";
import recorderEventBus from "@plugins/audio-recorder/service/event-bus";
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useStore } from "vuex";
import { NAMESPACE } from "@modules/attempt/service/attempt.constant.ts";
import AuthApi from "@apis/auth.api.ts";
import { useModal } from "@plugins/modal/composables/modal-instance.ts";
import { useVm } from "@utils/vm.ts";
import { useErrorHandler } from "@plugins/common/composables/error.ts";
import { useDirty } from "@plugins/common/composables/page-dirty.ts";
import { useEventBusWatch } from "@plugins/common/composables/event-bus-watch.ts";
import { useToast } from "@plugins/toast/composables/toast-instance.ts";
import EventBusUtils from "@plugins/common/service/event.bus.utils.ts";

// Props
interface Props {
  kind?: string;
  maxSliceOfDuration?: number;
  allowUpload?: (mediaType: string) => boolean;
  eventBusWatchInstances?: unknown[];
}

const {
  kind = "basic",
  maxSliceOfDuration = 600000,
  allowUpload,
  eventBusWatchInstances,
} = defineProps<Props>();

const { t } = useI18n();
const store = useStore();
const modal = useModal();
const toast = useToast();
const { vm } = useVm();
const { error, hasError, handleCloseError } = useErrorHandler();
const { trackWatch } = useEventBusWatch();

const dirtyId = ref("AudioRecorder");
const { setDirty, removeDirty } = useDirty(dirtyId);

// Data
const recorder = ref(null);
const interval = ref(null);
const recordingDuration = ref("00:00");
const isLoading = ref(true);
const isProcessing = ref(false);
const save = ref(false);
const realError = ref(false);
const pendingUpload = ref(false);
const waitingForConnection = ref(false);
const isCheckingConnection = ref(false);
const isUnmounted = ref(false);
const file = ref<File | null>(null);
const errorRemovalTimeout = ref(null);
const dataSaveButton = ref({
  kind: "primary",
  error: false,
  completed: false,
  iconOnly: true,
  tipAlignment: "end",
  tipPosition: "top",
});
const errorActionLabel = ref("");
const uploadErrorMessage = ref("");

// Computed
const iconName = computed(() => {
  return pendingUpload.value || waitingForConnection.value ? Upload24 : Stop24;
});

const errorTitle = computed(() => {
  return kind === "student_tinymce_3"
    ? t("audio_recorder_recording_error_title")
    : t("audio_recorder_se_recording_error_title");
});

const isRecording = computed(() => {
  return recorder.value && recorder.value.isRecording();
});

const checkNetworkConnected = computed<boolean>(
  () => store.getters[`${NAMESPACE.APP}/checkNetworkConnected`],
);

// Methods
const handleProcessError = () => {
  isProcessing.value = false;
  // Empty falls back to the button's generic error for blocked/unexpected failures
  uploadErrorMessage.value = checkNetworkConnected.value
    ? ""
    : t("audio_recorder_upload_error_message");
  dataSaveButton.value.error = true;
  pendingUpload.value = true;
  if (errorRemovalTimeout.value) clearTimeout(errorRemovalTimeout.value);
  errorRemovalTimeout.value = window.setTimeout(() => vm.$stickpanel.remove(), 3000);
};

const handleSaveButtonResetError = () => {
  dataSaveButton.value.error = false;
};

const handleOpenHelp = () => {
  const props = {
    title: t("audio_recorder_not_support_guide_title"),
    content: t("audio_recorder_not_support_guide_content"),
    primaryBtnLabel: t("button_ok"),
    use_secondaryButton: false,
  };
  modal.showMessage("warning", props);
};

const handleStop = () => {
  clearErrorMessage();

  // Check existence and type for allowUpload function
  if (!allowUpload || typeof allowUpload !== "function") {
    return;
  }

  if (!recorder.value.isRecording() && pendingUpload.value && file.value) {
    const canUpload = allowUpload(MEDIA_TYPE.AUDIO);
    pendingUpload.value = !canUpload;

    if (pendingUpload.value) {
      handleNotAllowToUpload();
      return;
    }

    dataSaveButton.value.error = false;
    isProcessing.value = true;
    recorderEventBus.$emit("recordingSaved", file.value);
    return;
  }

  pendingUpload.value = !allowUpload(MEDIA_TYPE.AUDIO);
  // reach max limit
  if (pendingUpload.value) {
    handleNotAllowToUpload();
    pendingUpload.value = true;
  }

  waitingForConnection.value = !checkNetworkConnected.value;

  if (recorder.value.isRecording()) {
    recorder.value.stopRecording();
    isProcessing.value = true;
  }
};

const handleOnInitSuccess = () => {
  isLoading.value = false;
  recorder.value.startRecording();
  setDirty(dirtyId.value);
};

const handleOnInitFail = (e) => {
  isLoading.value = false;
  realError.value = true;
  if (e.code === 1) {
    error.value.message = t("audio_recorder_not_support_title");
    error.value.subMessage = t("audio_recorder_not_support_message");
    errorActionLabel.value = "Learn more";
  } else if (e.code === 2) {
    error.value.message = t("audio_recorder_blocked_title");
  } else if (e.code === 3) {
    error.value.message = t("audio_recorder_not_detected_title");
    error.value.subMessage = t("audio_recorder_not_detected_message");
  }
};

const clearErrorMessage = () => {
  error.value.message = "";
  error.value.subMessage = "";
};

const handleNotAllowToUpload = () => {
  error.value.message = t("audio_recorder_validation_max_count_title");
  error.value.subMessage = t("audio_recorder_validation_max_count_message");
};

const handleOnStart = () => {
  save.value = true;
  interval.value = setInterval(() => {
    recordingDuration.value = recorder.value.recordingTimeDisplay();
  }, 1000);
};

const clearTimerInterval = () => {
  if (interval.value) {
    clearInterval(interval.value);
    interval.value = null;
  }
};

const handleOnStop = (info) => {
  clearTimerInterval();

  if (!save.value) {
    //if stop triggered by close audio recorder then dun emit save
    return;
  }

  let mimeType;

  if (!isEmpty(info.fileType) && info.fileType === "audio/mp3") {
    mimeType = "mp3";
  } else {
    if (window.MediaRecorder.isTypeSupported("audio/ogg")) {
      mimeType = "ogg";
    } else if (window.MediaRecorder.isTypeSupported("audio/webm")) {
      mimeType = "webm";
    } else if (window.MediaRecorder.isTypeSupported("audio/mp4")) {
      mimeType = "m4a";
    }
  }

  file.value = new File([info.blob], "recording." + mimeType, {
    type: info.blob.type,
  });

  if (maxSliceOfDuration * 0.001 < info.duration) {
    return;
  }

  if (waitingForConnection.value && checkNetworkConnected.value) {
    handleConnectionRestored(true);
  } else if (!pendingUpload.value && !waitingForConnection.value) {
    recorderEventBus.$emit("recordingSaved", file.value);
    isProcessing.value = true;
  }
};

const handleConnectionRestored = async (connected: boolean) => {
  if (
    !connected ||
    !waitingForConnection.value ||
    !file.value ||
    pendingUpload.value ||
    isCheckingConnection.value ||
    isUnmounted.value
  ) {
    return;
  }

  isCheckingConnection.value = true;
  try {
    const response = await AuthApi.pingSession();
    if (response?.code === "error") {
      return;
    }

    // The session check may finish after the recorder has been closed. Do not emit an upload
    // event for a component that no longer owns the recording.
    if (isUnmounted.value || !waitingForConnection.value || !file.value) {
      return;
    }

    waitingForConnection.value = false;
    recorderEventBus.$emit("recordingSaved", file.value);
  } catch {
    // Keep the recording pending until the connection watcher reports a usable session.
  } finally {
    isCheckingConnection.value = false;
  }
};

const handleClose = () => {
  if (realError.value) {
    vm.$stickpanel.remove();
    return;
  }
  const props = {
    title: t("audio_recorder_message_discard_confirmation_title"),
    content: t("audio_recorder_message_discard_confirmation_content"),
    primaryBtnLabel: t("button_delete"),
    secondaryBtnLabel: t("button_cancel"),
    actionPrimary: () => {
      save.value = false;
      if (recorder.value.isRecording()) recorder.value.stopRecording();
      vm.$stickpanel.remove();
    },
  };
  modal.showMessage("warning", props);
};

const showMaxLengthReached = () => {
  const formattedTime = recorder.value.convertToReadableTime(maxSliceOfDuration / 1000);
  // FIXME: move messages to locale
  toast.show("warning", {
    title: t("audio_recorder_max_duration_title", { formattedTime }),
    subTitle: t("audio_recorder_max_duration_subtitle"),
  });
};

const handleOnError = () => {
  vm.$stickpanel.remove();
  clearTimerInterval();

  toast.show("error", {
    kind: "error",
    title: errorTitle.value,
  });
};

onMounted(() => {
  // Register before microphone initialization so every asynchronous upload failure has a live
  // recorder handler, including failures that happen immediately after recording stops.
  trackWatch(recorderEventBus.$on("recordingSavedFailed", handleProcessError));

  recorder.value = new AudioRecorder({
    onInitSuccessCallBack: handleOnInitSuccess,
    onInitFailCallBack: handleOnInitFail,
    onStartCallBack: handleOnStart,
    onStopCallBack: handleOnStop,
    onRecordingError: handleOnError,
    maxSliceOfDuration: maxSliceOfDuration,
    reachedMaxChunk: showMaxLengthReached,
  });
  recorderEventBus.$emit("isRecording", true);
});

onBeforeUnmount(() => {
  isUnmounted.value = true;
  waitingForConnection.value = false;
  isCheckingConnection.value = false;

  // cater when user tries to record recording when already recording one
  if (recorder.value && recorder.value.mediaRecorder) {
    recorder.value.forceStop = true;
    recorder.value.mediaRecorder?.stop();
  }

  recorder.value = null;
  file.value = null;

  recorderEventBus.$emit("isRecording", false);
  clearTimerInterval();

  if (errorRemovalTimeout.value) clearTimeout(errorRemovalTimeout.value);

  removeDirty(dirtyId.value);

  EventBusUtils.unWatchInstance(eventBusWatchInstances);
});

watch(checkNetworkConnected, handleConnectionRestored);
</script>

<style lang="scss">
@import "@styles/layout/breakpoint";
@import "@styles/themes/sls-theme/tokens/colors";
@import "@styles/themes/sls-theme/tokens/spacing";
@import "@styles/themes/sls-theme/tokens/typo";

.audio-recorder {
  display: flex;
  flex-flow: row wrap;
  align-items: center;

  .bx--inline-notification {
    flex: 0 0 100%;
    min-width: 0;

    @include media("mobile-landscape") {
      flex-wrap: wrap;
    }

    .bx--inline-notification__close-button {
      display: none;
    }
  }

  .duration {
    @include carbon--type-style("body-short-01");
    display: inline-flex;
    align-items: center;

    @keyframes blinker {
      from {
        opacity: 1;
      }
      to {
        opacity: 0;
      }
    }

    &::before {
      content: "";
      display: inline-block;
      margin-right: $spacing-03;
      background-color: $audio-recorder-01;
      width: 9px;
      height: 9px;
      border-radius: 100%;
      animation: 1s linear 0s infinite alternate blinker;
    }
  }

  .tools {
    display: flex;
    align-items: center;
    margin-left: auto;
    gap: 0 $spacing-03;

    button {
      border: none;
      cursor: pointer;

      &:focus {
        outline: none;
      }

      &[disabled],
      &:disabled {
        cursor: not-allowed;
        opacity: 0.5;
      }

      &.btn-record {
        display: flex;
        align-items: center;

        path {
          fill: $ui-01;
        }
      }

      &.btn-close {
        color: $interactive-01;

        span {
          display: flex;
          align-items: center;
        }

        &:hover {
          color: $hover-primary;
        }

        path {
          stroke: currentColor;
        }
      }
    }

    .btn-stop {
      .bx--btn {
        background-color: $icon-03;
        color: $interactive-03;

        .sls-icon {
          [fill] {
            fill: currentColor;
          }
        }
      }
    }
  }
}
</style>
